import { BaseRepository } from './BaseRepository';
import Laboratory, { ILaboratory, Specialty } from '../models/Laboratory';
import Slot, { ISlot } from '../models/Slot';
import handleResponseError from './utils/handleResponseError';

export type LaboratoryParams = {};

export default class LaboratoryRepository extends BaseRepository<any> {
  static findAll(params: any[][] = []): Promise<Laboratory[]> {
    const requestOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...this.authHeader()
      }
    };

    var url = new URL(`${this.apiUrl}/laboratory`);
    url.search = new URLSearchParams(params).toString();

    return (
      fetch(url.toString(), requestOptions)
        .then(this.handleResponse)
        // .then(({ data }) => this.filterByAppointment(data))
        .then(({ data }) => {
          return data.map((item: ILaboratory) => new Laboratory(item));
        })
    );
  }

  static findByLatLng(
    latlng: {
      lat: number;
      lng: number;
    },
    params: any[][] = []
  ): Promise<Laboratory[]> {
    const requestOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...this.authHeader()
      }
    };

    var url = new URL(`${this.apiUrl}/laboratory`);
    const _params = [
      ['latitude', `${latlng.lat}`],
      ['longitude', `${latlng.lng}`],
      ...params
    ];
    url.search = new URLSearchParams(_params).toString();

    return (
      fetch(url.toString(), requestOptions)
        .then(this.handleResponse)
        // .then(({ data }) => this.filterByAppointment(data))
        .then(({ data }) => {
          return data.map((item: ILaboratory) => new Laboratory(item));
        })
    );
  }

  static findByName(
    query: string,
    params: any[][] = []
  ): Promise<Laboratory[]> {
    const requestOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...this.authHeader()
      }
    };

    var url = new URL(`${this.apiUrl}/laboratory`);
    const _params = [['search', query], ...params];
    url.search = new URLSearchParams(_params).toString();

    return (
      fetch(url.toString(), requestOptions)
        .then(this.handleResponse)
        // .then(({ data }) => this.filterByAppointment(data))
        .then(({ data }) => {
          return data.map((item: ILaboratory) => new Laboratory(item));
        })
    );
  }

  static findBySlug(
    query: string,
    params: any[][] = []
  ): Promise<Laboratory | null> {
    const requestOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...this.authHeader()
      }
    };

    var url = new URL(`${this.apiUrl}/laboratory`);
    const _params = [['slug', query], ['slug_match', 'exact'], ...params];
    url.search = new URLSearchParams(_params).toString();

    return (
      fetch(url.toString(), requestOptions)
        .then(this.handleResponse)
        // .then(({ data }) => this.filterByAppointment(data))
        .then(({ data }) => {
          if (data.length) {
            return new Laboratory(data[0]);
          } else {
            return null;
          }
        })
    );
  }

  static findMatchBySlug(
    query: string,
    params: any[][] = []
  ): Promise<Laboratory[]> {
    const requestOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...this.authHeader()
      }
    };

    var url = new URL(`${this.apiUrl}/laboratory`);
    const _params = [['slug', query], ...params];
    url.search = new URLSearchParams(_params).toString();

    return (
      fetch(url.toString(), requestOptions)
        .then(this.handleResponse)
        // .then(({ data }) => this.filterByAppointment(data))
        .then(({ data }) => {
          if (data.length) {
            return data.map((laboratory: any) => new Laboratory(laboratory));
          } else {
            return [];
          }
        })
    );
  }

  static getLaboratoriesByGroupSlug(
    slug: string,
    params: any[][] = []
  ): Promise<{
    laboratories: Laboratory[];
    checkinMessage?: string;
    welcomeMessage?: string;
    welcomeTitle?: string;
    specialty?: string;
  }> {
    const requestOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...this.authHeader()
      }
    };

    var url = new URL(`${this.apiUrl}/group/${slug}`);

    return (
      fetch(url.toString(), requestOptions)
        .then((res) => this.handleResponse(res, false))
        // .then(({ data }) => this.filterByAppointment(data))
        .then(({ data }) => {
          if (data?.laboratories.length) {
            return {
              laboratories: data.laboratories.map(
                (laboratory: any) => new Laboratory(laboratory)
              ),
              welcomeMessage: data.welcome_message || '',
              welcomeTitle: data.welcome_title || '',
              checkinMessage: data.checkin_message || '',
              specialty: data.speciality
            };
          } else {
            return { laboratories: [] };
          }
        })
        .catch(() => {
          return { laboratories: [] };
        })
    );
  }

  static getSlotsByLaboratory(
    laboratory: Laboratory,
    startDate?: Date
  ): Promise<Slot[]> {
    const requestOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...this.authHeader()
      }
    };

    var url = new URL(`${this.apiUrl}/laboratory/slots/${laboratory.id}`);
    const _params = [];

    if (startDate) {
      const offset = startDate.getTimezoneOffset();
      startDate = new Date(startDate.getTime() - offset * 60 * 1000);
      _params.push(['start', startDate.toISOString().split('T')[0]]);
    }
    url.search = new URLSearchParams(_params).toString();

    return fetch(url.toString(), requestOptions)
      .then(this.handleResponse)
      .then(({ data }) => {
        return data.map((item: ISlot) => new Slot(item));
      });
  }

  static getSpecialties(): Promise<Specialty[]> {
    const requestOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...this.authHeader()
      }
    };

    var url = new URL(`${this.apiUrl}/laboratory/specialities`);

    return fetch(url.toString(), requestOptions)
      .then(this.handleResponse)
      .then((data) => {
        return data;
      });
  }

  static filterByAppointment(data: ILaboratory[]) {
    return data.filter((item: ILaboratory) => item.appointment_type !== 'none');
  }

  static handleResponse(response: Response, showError = true) {
    return response.text().then((text: string) => {
      const res = text && JSON.parse(text);
      if (!response.ok && showError) {
        const error = (res && res.message) || response.statusText;
        let errorString = '';

        if (res.errors) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          for (let [key, value] of Object.entries(res.errors)) {
            errorString += `${value}<br/><br/>`;
          }
        }

        handleResponseError(response, res, errorString || error);
        return Promise.reject(error);
      }

      return res;
    });
  }
}
